<template>
    <div class="page-guichet page-guichet--ticket">
        <SidebarVue />
        <div class="content">
            <StatusBar title="Guichet" />
            <div class="content_inner">
                <div class="content_inner_inner">

                    <div id="chart">
                    </div>

                    <div class="bottom-actions">
                        <Button variant="secondary" icon="fast" size="small" :full-width="false" @click="switchToAuto()">
                            Placement automatique
                        </Button>
                    </div>
                </div>
                <Cart />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import SidebarVue from '@/components/organisms/Sidebar.vue'
import StatusBar from '@/components/organisms/StatusBar.vue'
import Cart from '@/components/organisms/Cart.vue'
import { computed, onMounted, ref } from 'vue'
import { useUserStore } from '@/stores/UserStore'
import { useEventStore } from '@/stores/EventStore'
import { AppPrice } from '@/tools/Price'
import { useRouter } from 'vue-router'
import Button from '@/components/atoms/Button.vue'
import { TicketsPerCategory, useTicketTypeStore } from '@/stores/TicketTypeStore'
import { useCartStore } from '@/stores/CartStore'
import { toast } from 'vue3-toastify'
import { useAppStore } from '@/stores/AppStore'
import { useI18n } from 'vue-i18n'

    const isSeatsIoReady = ref(false)
    const seatsIoChartInstance = ref<any>(null)
    const userStore = useUserStore()
    const appStore = useAppStore()
    const eventStore = useEventStore()
    const router = useRouter()
    const ticketTypeStore = useTicketTypeStore()
    const cartStore = useCartStore()
    const { t } = useI18n()

    onMounted(async () => {
        cartStore.isBookingSeats = false
        const seatsIoScript = document.createElement("script");
        seatsIoScript.setAttribute("src", "https://cdn-eu.seatsio.net/chart.js")
        document.head.appendChild(seatsIoScript);
        const interval = setInterval(() => {
            if ('seatsio' in window) {
                isSeatsIoReady.value = true
                initSeatsIo(window.seatsio)
                clearInterval(interval);
            }
        })
    })

    const seatsIoeventId = computed(() => {
        if (eventStore.currentSession) {
            return eventStore.currentSession._id
        }
        if (eventStore.currentEvent) {
            return eventStore.currentEvent._id
        }
        return false
    })


    const initSeatsIo = (seatsio: any) => {
        const pricing = []

        if (ticketTypeStore.ticketsPerCategory.length) {
            let index = 0
            ticketTypeStore.ticketsPerCategory.forEach((category: TicketsPerCategory) => {
                if (category.category) {
                    const seatsIoCategory = {
                        category: category.category.seatsIoCategoryId,
                        ticketTypes: category.tickets.map(ticket => {
                            return {
                                ticketType: ticket._id,
                                label: ticket.name,
                                price: ticket.price,
                            }
                        })
                    }
                    pricing.push(seatsIoCategory)
                }
                index += 1
            })
        }

        const channels = ['NO_CHANNEL', ...eventStore.currentEvent?.ticketOffice?.seatsIoChannels || []]

        const quotaEffective = cartStore.quotaPerOrderEffective

        const chartConfig: any = {
            divId: 'chart',
            priceFormatter: (price: number) => {
                return new AppPrice(price).divide100().toString()
            },
            pricing,
            channels,
            showFullScreenButton: true,
            workspaceKey: userStore.organisation?.seatsIoWorkspace,
            event: seatsIoeventId.value,
            onObjectSelected: (obj: any) => {
                // Garde défensive : bloquer la sélection si quota dépassé
                // (ne devrait pas arriver si maxSelectedObjects est passé,
                // mais agit en protection en profondeur).
                if (cartStore.isQuotaReached) {
                    toast.warning(t('quotaPerOrder.seats_exceeded', { quota: cartStore.quotaPerOrderEffective }))
                    // Demander à Seats.io de désélectionner l'objet
                    seatsIoChartInstance.value?.deselect(obj.id)
                    return
                }
                const selectedTicketType = ticketTypeStore.ticketTypes.find(item => {
                    return (item._id == obj.selectedTicketType)
                })
                if (selectedTicketType && obj.label) {
                    cartStore.addTicket(selectedTicketType, obj.label)
                }
            },
            onObjectDeselected: (obj: any, deselectedTicketType) => {
                // On retire la place du panier si déselection manuelle ou
                // si la place a été bookée par qq1 d'autres.
                const needToRemove = (obj.status == 'free' || (obj.status !== 'free' && !cartStore.isBookingSeats))
                if (needToRemove) {
                    const selectedTicketType = ticketTypeStore.ticketTypes.find(item => {
                        return (item._id == deselectedTicketType.ticketType)
                    })
                    if (selectedTicketType) {
                        cartStore.removeTicket(selectedTicketType, obj.label)
                    }
                    if (obj.status !== 'free') {
                        toast.error(`La place ${obj.label} vient d'être réservée et a été retirée du panier.`)
                    }
                }
            }
        }

        // Passer maxSelectedObjects à Seats.io si un quota est configuré.
        // Seats.io bloquera nativement la sélection au-delà de la limite.
        // Le message natif de Seats.io est en anglais : on le surcharge via
        // l'option `messages` pour afficher un message en français.
        if (quotaEffective !== null) {
            chartConfig.maxSelectedObjects = quotaEffective
            chartConfig.messages = {
                maxSelectedObjectsReached: t('quotaPerOrder.seats_exceeded', { quota: quotaEffective })
            }
        }

        seatsIoChartInstance.value = new seatsio.SeatingChart(chartConfig).render();
    }

    const switchToAuto = (async () => {
        await cartStore.cancel()
        router.push({name: 'ticket', params: { eventId: eventStore.currentEvent?._id }})
    })

</script>